import React, { createContext, useState, useContext, useEffect } from 'react';
import config, { api } from '../config/api';

// Create the context
const AuthContext = createContext();

// Helper to get the stored token
const getAuthToken = () => localStorage.getItem('auth_token');

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if user is authenticated on initial load
  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Get token from localStorage if it exists
        const token = getAuthToken();
        
        if (token) {  
          // Get current user info using the authenticated api instance
          const response = await api.get('/auth/me');
          
          if (response.data.success) {
            setCurrentUser(response.data.data);
            setIsAuthenticated(true);
          }
        }
      } catch (err) {
        console.error('Auth check error:', err);
        // If auth fails, clear token
        localStorage.removeItem('auth_token');
        setError('Authentication failed. Please log in again.');
      } finally {
        setLoading(false);
      }
    };
    
    checkAuth();
  }, []);

  // Login function - we're using Google OAuth, so this redirects to Google
  const login = () => {
    window.location.href = `${config.API_BASE_URL}/auth/google`;
  };

  // Handle auth callback after Google OAuth
  const handleAuthCallback = (token = null) => {
    // If a token is provided (from URL), store it
    if (token) {
      localStorage.setItem('auth_token', token);
    }
    
    // Get user info after OAuth callback using authenticated api instance
    api.get('/auth/me')
      .then(response => {
        if (response.data.success) {
          setCurrentUser(response.data.data);
          setIsAuthenticated(true);
        }
      })
      .catch(err => {
        console.error('Error getting user info:', err);
        setError('Failed to get user info after login.');
        // If authentication fails, clear token
        localStorage.removeItem('auth_token');
      });
  };

  // Logout function
  const logout = async () => {
    try {
      // Try to logout on server (may not be necessary with JWT)
      await api.post('/auth/logout');
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      // Always clear local state and token even if server logout fails
      localStorage.removeItem('auth_token');
      setCurrentUser(null);
      setIsAuthenticated(false);
    }
  };

  // Update user profile (specifically Monzo username)
  const updateMonzoUsername = async (monzoUsername) => {
    try {
      const response = await api.patch('/auth/me/monzo', { monzoUsername });
      
      if (response.data.success) {
        setCurrentUser(prev => ({
          ...prev,
          monzoUsername
        }));
        return { success: true };
      } else {
        return { success: false, message: response.data.message };
      }
    } catch (err) {
      console.error('Error updating Monzo username:', err);
      return { 
        success: false, 
        message: err.response?.data?.message || 'Failed to update Monzo username'
      };
    }
  };

  // Value to be provided to consumers
  const value = {
    currentUser,
    isAuthenticated,
    loading,
    error,
    login,
    logout,
    handleAuthCallback,
    updateMonzoUsername
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = () => useContext(AuthContext);
